import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import Layout from '@/components/Layout';
import { useLanguage } from '@/context/LanguageContext';
import { Button } from '@/components/ui/button';
import { ArrowRight, MapPin, Calendar, Heart, Users, Globe, Lightbulb } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Experience = () => {
  const { t, language } = useLanguage();
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchArticles = async () => {
      try {
        const response = await axios.get(`${API}/articles?category=experience&published_only=true`);
        setArticles(response.data);
      } catch (error) {
        console.error('Error fetching articles:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchArticles();
  }, []);

  const topics = [
    {
      icon: MapPin,
      title: t('Mon arrivée', 'Meine Ankunft'),
      description: t('Premières impressions et découverte du pays', 'Erste Eindrücke und Entdeckung des Landes'),
      color: 'bg-blue-100 text-blue-600'
    },
    {
      icon: Globe,
      title: t('La culture allemande', 'Die deutsche Kultur'),
      description: t('Ce qui m\'a surpris, fasciné ou dérouté', 'Was mich überrascht, fasziniert oder verwirrt hat'),
      color: 'bg-yellow-100 text-yellow-600'
    },
    {
      icon: Calendar,
      title: t('Le quotidien', 'Der Alltag'),
      description: t('La langue, les habitudes, les différences', 'Die Sprache, die Gewohnheiten, die Unterschiede'),
      color: 'bg-green-100 text-green-600'
    },
    {
      icon: Heart,
      title: t('Moments de doute', 'Momente des Zweifels'),
      description: t('La solitude et comment je l\'ai surmontée', 'Die Einsamkeit und wie ich sie überwunden habe'),
      color: 'bg-red-100 text-red-500'
    },
    {
      icon: Users,
      title: t('Les rencontres', 'Die Begegnungen'),
      description: t('Les personnes qui ont marqué mon séjour', 'Die Menschen, die meinen Aufenthalt geprägt haben'),
      color: 'bg-purple-100 text-purple-600'
    },
    {
      icon: Lightbulb,
      title: t('Ce que j\'ai appris', 'Was ich gelernt habe'),
      description: t('Les leçons humaines de cette aventure', 'Die menschlichen Lektionen dieses Abenteuers'),
      color: 'bg-orange-100 text-orange-600'
    }
  ];

  return (
    <Layout>
      {/* Hero Section */}
      <section className="relative py-20 md:py-32 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src="https://images.unsplash.com/photo-1754386325173-86ea4b11ab98?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODB8MHwxfHNlYXJjaHwxfHxzY2VuaWMlMjBnZXJtYW55JTIwbGFuZHNjYXBlJTIwYmF2YXJpYSUyMGZvcmVzdHxlbnwwfHx8fDE3NjgzOTAyOTl8MA&ixlib=rb-4.1.0&q=85"
            alt="Germany landscape"
            className="w-full h-full object-cover opacity-20"
          />
        </div>

        <div className="relative max-w-7xl mx-auto px-6 md:px-12 text-center">
          <span className="text-xs font-bold tracking-widest uppercase text-blue-600 mb-4 block animate-fadeInUp">
            {t('Récits de voyage', 'Reiseberichte')}
          </span>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-slate-900 leading-tight mb-6 animate-fadeInUp delay-100 font-outfit">
            {t('Mon expérience', 'Meine Erfahrung')}
            <br />
            <span className="text-blue-600">{t('en Allemagne', 'in Deutschland')}</span>
          </h1>
          <p className="text-lg md:text-xl text-slate-600 max-w-2xl mx-auto leading-relaxed animate-fadeInUp delay-200">
            {t(
              "Je raconte mon expérience en Allemagne étape par étape. Chaque article correspond à une période, une émotion ou une leçon apprise sur place.",
              "Ich erzähle von meiner Erfahrung in Deutschland, Schritt für Schritt. Jeder Artikel entspricht einer Periode, einer Emotion oder einer Lektion, die ich vor Ort gelernt habe."
            )}
          </p>
        </div>
      </section>

      {/* Topics Grid */}
      <section className="py-20 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 font-outfit">
              {t('Les thèmes que j\'aborde', 'Die Themen, die ich behandle')}
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {topics.map((topic, index) => (
              <div 
                key={index}
                className="bg-slate-50 border border-slate-200 rounded-2xl p-8 card-hover"
                data-testid={`topic-card-${index}`}
              >
                <div className={`w-14 h-14 ${topic.color} rounded-xl flex items-center justify-center mb-6`}>
                  <topic.icon className="w-7 h-7" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-3 font-outfit">
                  {topic.title}
                </h3>
                <p className="text-slate-600 leading-relaxed">
                  {topic.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Articles Section */}
      <section className="py-20 md:py-32 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <span className="text-xs font-bold tracking-widest uppercase text-blue-600 mb-4 block">
              {t('Articles', 'Artikel')}
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 font-outfit">
              {t('Mes récits d\'expérience', 'Meine Erfahrungsberichte')}
            </h2>
          </div>

          {loading ? (
            <div className="flex justify-center">
              <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : articles.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {articles.map((article, index) => (
                <Link 
                  key={article.id} 
                  to={`/blog/${article.slug}`}
                  className="group"
                  data-testid={`experience-article-${index}`}
                >
                  <article className="bg-white border border-slate-200 rounded-2xl overflow-hidden card-hover h-full">
                    {article.image_url && (
                      <div className="aspect-video overflow-hidden">
                        <img
                          src={article.image_url}
                          alt={language === 'fr' ? article.title_fr : (article.title_de || article.title_fr)}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                      </div>
                    )}
                    <div className="p-8">
                      <h3 className="text-xl font-bold text-slate-900 mb-4 font-outfit group-hover:text-blue-600 transition-colors">
                        {language === 'fr' ? article.title_fr : (article.title_de || article.title_fr)}
                      </h3>
                      <p className="text-slate-600 leading-relaxed line-clamp-3">
                        {language === 'fr' ? article.excerpt_fr : (article.excerpt_de || article.excerpt_fr)}
                      </p>
                      <div className="mt-6 flex items-center text-blue-600 font-medium">
                        {t('Lire la suite', 'Weiterlesen')}
                        <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-2 transition-transform" />
                      </div>
                    </div>
                  </article>
                </Link>
              ))}
            </div>
          ) : (
            <div className="text-center py-16 bg-white rounded-2xl border border-slate-200">
              <p className="text-slate-600 text-lg mb-6">
                {t(
                  "Les articles arrivent bientôt ! En attendant, découvre mes conseils.",
                  "Artikel kommen bald! In der Zwischenzeit entdecke meine Tipps."
                )}
              </p>
              <Link to="/tips">
                <Button className="btn-primary rounded-full" data-testid="experience-empty-cta">
                  {t('Voir les conseils', 'Tipps ansehen')}
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 md:py-32 bg-blue-600">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6 font-outfit">
            {t('Envie d\'en savoir plus ?', 'Möchtest du mehr erfahren?')}
          </h2>
          <p className="text-xl text-blue-100 mb-10">
            {t(
              "Découvre mes conseils pratiques pour préparer ton propre voyage.",
              "Entdecke meine praktischen Tipps, um deine eigene Reise vorzubereiten."
            )}
          </p>
          <Link to="/tips">
            <Button 
              size="lg"
              className="btn-secondary rounded-full px-10 py-6 text-base font-bold"
              data-testid="experience-cta"
            >
              {t('Conseils pour partir', 'Tipps zum Aufbrechen')}
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>
      </section>
    </Layout>
  );
};

export default Experience;
